import React, { useState, useEffect, useCallback } from 'react';
import { UserContext, User, UserContextType } from '@mfox/shared/contexts/UserContext';
import * as studentService from '../api/studentService';
import { initKeycloak, keycloakAuth, keycloakLogin, keycloakLogout, keycloakToken } from '../auth/keycloak';
import { toAppUser } from './studentServiceUser';

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState<string | null>(null);

  // Restore session on mount: from Keycloak, or from localStorage for the portal's own login
  useEffect(() => {
    if (keycloakAuth) {
      let cancelled = false;
      initKeycloak()
        .then(async (signedIn) => {
          const token = keycloakToken();
          if (!signedIn || !token) return;
          try {
            const account = await studentService.me(token);
            if (!cancelled) setCurrentUser(toAppUser(account));
          } catch (err) {
            // 403: signed in at Keycloak, but this account may not use the portal. Say why instead of looping.
            if (err instanceof studentService.StudentServiceError && err.status === 403) {
              if (!cancelled) setAccessError(err.message);
            } else {
              throw err;
            }
          }
        })
        .catch(() => undefined) // Keycloak or the backend is unreachable: stay signed out
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
    const stored = localStorage.getItem('userSession');
    if (stored) {
      try {
        const { user } = JSON.parse(stored);
        setCurrentUser(user);
      } catch {
        // Ignore parse errors, treat as no session
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (keycloakAuth) {
      // Keycloak asks for the email and password on its own page.
      await keycloakLogin();
      return;
    }
    const session = await studentService.login(email, password);
    const user = toAppUser(session.user);
    setCurrentUser(user);
    localStorage.setItem('userSession', JSON.stringify({ user, token: session.token }));
  }, []);

  const activateAccount = useCallback(async (code: string, password: string, phoneNumber: string) => {
    const session = await studentService.activate(code, password, phoneNumber);
    const user = toAppUser(session.user);
    setCurrentUser(user);
    localStorage.setItem('userSession', JSON.stringify({ user, token: session.token }));
  }, []);

  /** The account the server sent back after a change becomes the signed-in user. */
  const adoptAccount = useCallback((account: studentService.StudentServiceUser) => {
    const user = toAppUser(account);
    setCurrentUser(user);
    // Under Keycloak the session lives there; a stored copy would only go stale.
    if (!keycloakAuth) {
      try {
        localStorage.setItem('userSession', JSON.stringify({ user, token: studentService.sessionToken() ?? undefined }));
      } catch {
        // Storage full or blocked: the save went through, it just won't survive a reload.
      }
    }
  }, []);

  const completeOnboarding = useCallback(async (skills: string[], aspiration: string) => {
    if (!currentUser) throw new Error('Not logged in');
    adoptAccount(await studentService.saveOnboarding(skills, aspiration));
  }, [currentUser, adoptAccount]);

  const updateProfile = useCallback(async (changes: studentService.ProfileChanges) => {
    if (!currentUser) throw new Error('Not logged in');
    adoptAccount(await studentService.updateProfile(changes));
  }, [currentUser, adoptAccount]);

  const logout = useCallback(() => {
    if (keycloakAuth) {
      setCurrentUser(null);
      void keycloakLogout();
      return;
    }
    const token = studentService.sessionToken();
    if (token) studentService.logout(token).catch(() => undefined);
    setCurrentUser(null);
    localStorage.removeItem('userSession');
  }, []);

  const isAuthorized = useCallback((requiredRoles: string | string[]): boolean => {
    if (!currentUser) return false;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return roles.includes(currentUser.role);
  }, [currentUser]);

  const value: UserContextType = {
    currentUser,
    isLoading,
    accessError,
    login,
    logout,
    isAuthorized,
    completeOnboarding,
    updateProfile,
    activateAccount,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}
